import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  TextField
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Save01 } from 'untitledui-js-base';
import { AccidentServiceRequest, OpenServices, RegistryIdName } from '../types/accident';

interface AccidentAssigneeServiceDialogProps extends Omit<DialogProps, 'onSubmit'> {
  services?: RegistryIdName[];
  defaultData?: OpenServices;
  defaultDamageNote?: string;
  isLoadingServices?: boolean;
  fetchServices?: () => void;
  assigneeServiceToAccident?: ({
    data,
    isUpdate
  }: {
    data: AccidentServiceRequest;
    isUpdate?: boolean;
  }) => void;
  isSaving?: boolean;
}

export const AccidentAssigneeServiceDialog = ({
  open,
  onClose,
  services,
  defaultData,
  defaultDamageNote,
  isLoadingServices,
  fetchServices,
  assigneeServiceToAccident,
  isSaving,
  ...props
}: AccidentAssigneeServiceDialogProps) => {
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [selectedService, setSelectedService] = useState<RegistryIdName | null>(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<AccidentServiceRequest>({
    defaultValues: {
      sourceId: undefined,
      note: defaultData?.note ?? '',
      serviceInfo: defaultData?.serviceInfo ? defaultData?.serviceInfo : defaultDamageNote ? defaultDamageNote : ''
    }
  });

  useEffect(() => {
    reset?.({
      note: defaultData?.note ?? '',
      serviceInfo: defaultData?.serviceInfo ? defaultData?.serviceInfo : defaultDamageNote ? defaultDamageNote : ''
    });

    if (!services || services.length === 0) {
      setSelectedService(null);
      return;
    }

    const match = services.find((s) => s.id === defaultData?.sourceId) ?? null;

    setSelectedService(match);
  }, [defaultData, services, reset]);

  const handleOpen = () => {
    setOpenAutocomplete(true);
  };

  const handleClose = () => {
    setOpenAutocomplete(false);
  };

  const onSubmit = (data: AccidentServiceRequest) => {
    if (!selectedService) return null;
    assigneeServiceToAccident?.({
      data: {
        sourceId: selectedService.id,
        note: data.note,
        serviceInfo: data.serviceInfo
      },
      isUpdate: !isEmpty(defaultData)
    });
    reset();
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="xl" {...props}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 16 }}>
        <DialogTitle id="alert-dialog-title">Přiřazení / úprava servisu</DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Stack spacing={2} mt={2}>
            <Autocomplete
              open={openAutocomplete}
              onOpen={handleOpen}
              onClose={handleClose}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              options={services ?? []}
              value={selectedService}
              loading={isLoadingServices}
              onChange={(_, value) => setSelectedService(value)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vyberte servis..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingServices ? <CircularProgress color="primary" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="note"
              render={({ field: { onChange, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  label="Poznámka"
                  color="secondary"
                  fullWidth
                  error={Boolean(errors.note)}
                  helperText={errors.note?.message}
                  InputLabelProps={{ shrink: true }}
                  multiline
                  rows={5}
                />
              )}
            />
            <Controller
              control={control}
              name="serviceInfo"
              render={({ field: { onChange, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  label="Objednané servisní práce"
                  color="secondary"
                  fullWidth
                  error={Boolean(errors.serviceInfo)}
                  helperText={errors.serviceInfo?.message}
                  InputLabelProps={{ shrink: true }}
                  multiline
                  rows={5}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            py: 4,
            justifyContent: 'center',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            gap: 2
          }}
        >
          <Button
            type="button"
            onClick={() => {
              reset();
              onClose?.({}, 'escapeKeyDown');
            }}
          >
            Zrušit
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save01 />}
            size="large"
            color="success"
            loading={isSaving}
          >
            Uložit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
